/**
 * Unified grid-cell style for the simulator map.
 *
 * Single red hue (#ef4444) with fill opacity driven by utilization_ratio:
 *   - Unallocated (u=0 / null)  → fully hollow (fillOpacity 0)
 *   - Just allocated (u>0)      → pops in at FILL_MIN_OPACITY
 *   - Fully utilized (u=1)      → FILL_MAX_OPACITY
 *
 * Stroke shares the same red, fading in between zoom 4→6.
 */
export const CELL_COLOR = '#ef4444';
export const FILL_MIN_OPACITY = 0.15;
export const FILL_MAX_OPACITY = 0.85;
const STROKE_FADE_MIN_ZOOM = 4;
const STROKE_FADE_MAX_ZOOM = 6;
const STROKE_MAX_OPACITY = 0.5;

export const getCellFillOpacity = (u) => {
  if (!Number.isFinite(u) || u <= 0) return 0;
  const c = Math.max(0, Math.min(1, u));
  return FILL_MIN_OPACITY + (FILL_MAX_OPACITY - FILL_MIN_OPACITY) * c;
};

export const getCellStrokeOpacity = (zoom) => {
  const span = STROKE_FADE_MAX_ZOOM - STROKE_FADE_MIN_ZOOM;
  const z = zoom ?? STROKE_FADE_MIN_ZOOM;
  const t = span > 0
    ? Math.max(0, Math.min(1, (z - STROKE_FADE_MIN_ZOOM) / span))
    : (z >= STROKE_FADE_MAX_ZOOM ? 1 : 0);
  return STROKE_MAX_OPACITY * t;
};

export const buildSimulatorMapCellTooltipHtml = (info, { cellId, techLabel } = {}) => {
  const fmt = (v) => (Number.isFinite(v) ? v.toFixed(2) : '—');
  const fmtGw = (v) => (Number.isFinite(v) ? v.toFixed(3) : '—');
  if (info?.source === 'vo_cluster') {
    const uPct = Number.isFinite(info.utilization_ratio)
      ? `${(info.utilization_ratio * 100).toFixed(0)}%`
      : '—';
    return `<div style="line-height: 1.5;">
      ${techLabel ? `<div><strong>${techLabel}</strong></div>` : ''}
      <div><strong>Cell:</strong> ${cellId || 'N/A'}</div>
      <div><strong>VO cluster:</strong> ${info.processset || '—'}</div>
      <div><strong>Cluster capacity:</strong> ${fmtGw(info.cluster_gw)} GW</div>
      <div><strong>Resource (cell):</strong> ${fmt(info.capacity_mw)} MW</div>
      <div><strong>Utilization:</strong> ${uPct}</div>
      <div><strong>CF:</strong> ${fmt(info.capacity_factor)}</div>
    </div>`;
  }
  const totalCap = info?.capacity_mw;
  const uRatio = info?.utilization_ratio;
  const cf = info?.capacity_factor;
  const installed = (Number.isFinite(totalCap) && Number.isFinite(uRatio))
    ? totalCap * uRatio : null;
  const remaining = (Number.isFinite(totalCap) && Number.isFinite(installed))
    ? Math.max(0, totalCap - installed) : null;
  return `<div style="line-height: 1.5;">
    ${techLabel ? `<div><strong>${techLabel}</strong></div>` : ''}
    <div><strong>Cell:</strong> ${cellId || 'N/A'}</div>
    <div><strong>Installed:</strong> ${fmt(installed)} MW</div>
    <div><strong>Remaining:</strong> ${fmt(remaining)} MW</div>
    <div><strong>CF:</strong> ${fmt(cf)}</div>
  </div>`;
};

export const getCellStyle = (utilizationRatio, zoom) => ({
  color: CELL_COLOR,
  weight: 0.6,
  opacity: getCellStrokeOpacity(zoom),
  fillColor: CELL_COLOR,
  fillOpacity: getCellFillOpacity(utilizationRatio),
});
